import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";


const Demo = () => {
  const { t } = useTranslation();
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();


  const [selectedType, setSelectedType] = useState("");
  const [customization, setCustomization] = useState({});
  const [options, setOptions] = useState({ 
    delivery: false, 
    address: "", 
    assembly: false 
  });
  const [cost, setCost] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);

  // Učitaj Google Maps API skriptu
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBDgiqSTEJ1WJ-2IaESi4sh5_uGhjOxm7Y&libraries=places`;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);

      script.onload = () => {
        console.log('Google Maps API loaded');
      };
    }
  }, []);

  // Inicijalizacija autocomplete-a kada je delivery uključen
  useEffect(() => {
    if (options.delivery && window.google && !autocomplete && inputRef.current) {
      const newAutocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['geocode'],
          componentRestrictions: { country: 'rs' }, // Ograniči na Srbiju
        }
      );
      
      newAutocomplete.addListener('place_changed', () => {
        const place = newAutocomplete.getPlace();
        if (place.formatted_address) {
          setOptions(prev => ({
            ...prev,
            address: place.formatted_address
          }));
        }
      });
      
      setAutocomplete(newAutocomplete);
    }

    return () => {
      if (autocomplete) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [options.delivery, autocomplete]);

  const calculateCost = () => {
    let total = 100;
    if (options.delivery) total += 30;
    if (options.assembly) total += 50;
    setCost(total);
  };

  const kitchenTypes = [
    {
      id: "line",
      image: "/demo/thumbnails/line.png",
      glb: "/demo/3dmodels/line.glb",
      code: "line"
    },
    {
      id: "corner",
      image: "/demo/thumbnails/corner.png",
      glb: "/demo/3dmodels/corner.glb",
      code: "corner"
    },
    {
      id: "lineisland",
      image: "/demo/thumbnails/lineisland.png",
      glb: "/demo/3dmodels/lineisland.glb",
      code: "lineisland"
    },
    {
      id: "corner2",
      image: "/demo/thumbnails/corner2.png",
      glb: "/demo/3dmodels/corner2.glb",
      code: "corner2"
    }
  ];   

  return (
    <div className="p-6 space-y-12">
      {/* Step 1: What you need? */}
      <section>
        <h2 className="text-2xl font-bold mb-4">{t("demo.what_you_need")}</h2>
        <div className="grid gap-4 grid-cols-[repeat(auto-fit,_minmax(320px,_1fr))]">
        { kitchenTypes.map((type) => (
  <div
    key={type.id}
    onClick={() => {
      setSelectedType(type.id);
      navigate(`/demo3d/${type.code}`);
    }}
    className={`relative border rounded shadow group transition-all duration-300 cursor-pointer 
      ${selectedType === type.id ? "bg-primary-light text-white dark:text-white" : "bg-primary-light text-black dark:text-white"}`}
    style={{ overflow: "visible" }}
  >
    <div className="relative z-10 overflow-visible">
      <img
        src={type.image}
        alt={t(`demo.type.${type.id}`)}
        draggable={false}
        className="w-full h-48 object-contain transform transition-transform duration-300 group-hover:scale-150 group-hover:z-[9999] drop-shadow-xl select-none pointer-events-none"
      />
    </div>
    <div className="text-center py-2 font-medium relative z-20">
      {t(`demo.type.${type.id}`)}
    </div>
  </div>
))}
</div>

      </section>

      {selectedType && (
        <>
          {/* Step 2: Custom made */}
            <section>
              <h2 className="text-2xl font-bold mb-4 text-text-light dark:text-text-dark">
                {t("demo.custom_made")}
              </h2>
              <input
                type="text"
                placeholder={t("demo.enter_dimensions")}
                onChange={(e) => setCustomization({ ...customization, dimensions: e.target.value })}
                className="w-full border px-4 py-2 rounded bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark border-gray-200 dark:border-neutral-700"
              />
            </section>

            {/* Step 3: Options */}
            <section>
              <h2 className="text-2xl font-bold mb-4 text-text-light dark:text-text-dark">
                {t("demo.options")}
              </h2>

              <label className="block text-text-light dark:text-text-dark">
                <input
                  type="checkbox"
                  checked={options.delivery}
                  onChange={() => setOptions({ ...options, delivery: !options.delivery })}
                  className="mr-2 bg-background-light dark:bg-background-dark border-gray-200 dark:border-neutral-700 text-primary-light dark:text-primary-dark"
                />
                {t("demo.delivery")}
              </label>

              {options.delivery && (
                <div className="mt-2">
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder={t("demo.enter_address")}
                    className="w-full border px-4 py-2 rounded bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark border-gray-200 dark:border-neutral-700 placeholder-gray-400 dark:placeholder-gray-500"
                    id="address-input"
                  />
                </div>
              )}

              <label className="block mt-2 text-text-light dark:text-text-dark">
                <input
                  type="checkbox"
                  checked={options.assembly}
                  onChange={() => setOptions({ ...options, assembly: !options.assembly })}
                  className="mr-2 bg-background-light dark:bg-background-dark border-gray-200 dark:border-neutral-700 text-primary-light dark:text-primary-dark"
                />
                {t("demo.assembly")}
              </label>

              {options.address && (
                <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                  {t("demo.selected_address")}: {options.address}
                </p>
              )}
            </section>

          {/* Step 4: Cost calc */}
          <section>
            <h2 className="text-2xl font-bold mb-4">{t("demo.cost_calc")}</h2>
            <button
              onClick={calculateCost}
              className="bg-primary-light text-white px-6 py-2 rounded"
            >
              {t("demo.calculate")}
            </button>
            {cost !== null && (
              <div className="mt-4 text-lg font-semibold">
                {t("demo.total_cost")}: {cost} €
                <p>{t("demo.payment_plan")}: {(cost / 6).toFixed(2)} €/month</p>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default Demo;